import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, CalendarClock, Clock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

const DAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const EMPTY_OV = { date: "", end_date: "", mode: "closed", start: "10:00", end: "14:00", note: "" };

export default function OpeningHours() {
  const [hours, setHours] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [ov, setOv] = useState(EMPTY_OV);

  const load = () => api.get("/admin/opening-hours").then((r) => {
    setHours(r.data.opening_hours.sort((a, b) => a.day - b.day).map((h) => ({
      ...h, blocks: (h.blocks && h.blocks.length) ? h.blocks : [{ start: h.start || "09:00", end: h.end || "18:00" }],
    })));
    setOverrides(r.data.overrides);
  });
  useEffect(() => { load(); }, []);

  const setDay = (day, patch) => setHours(hours.map((h) => (h.day === day ? { ...h, ...patch } : h)));
  const setBlock = (day, i, patch) => setHours(hours.map((h) => h.day === day ? { ...h, blocks: h.blocks.map((b, bi) => bi === i ? { ...b, ...patch } : b) } : h));
  const addBlock = (day) => setHours(hours.map((h) => h.day === day ? { ...h, blocks: [...h.blocks, { start: "13:00", end: "17:00" }] } : h));
  const removeBlock = (day, i) => setHours(hours.map((h) => h.day === day ? { ...h, blocks: h.blocks.filter((_, bi) => bi !== i) } : h));

  const saveHours = async () => {
    try {
      await api.put("/admin/opening-hours", { opening_hours: hours });
      toast.success("Openingstijden opgeslagen.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addOverride = async () => {
    if (!ov.date) { toast.error("Kies een datum."); return; }
    try {
      const { data } = await api.post("/admin/overrides", ov);
      toast.success(data.created > 1 ? `${data.created} dagen toegevoegd.` : "Uitzondering toegevoegd.");
      setOv(EMPTY_OV); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delOverride = async (id) => { await api.delete(`/admin/overrides/${id}`); toast.success("Uitzondering verwijderd."); load(); };

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-medium tracking-tight mb-1">Openingstijden</h1>
      <p className="text-ink-muted mb-8">Bepaal per dag wanneer je werkt en blokkeer of open losse dagen.</p>

      {/* Weekly hours */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <h2 className="text-xl font-medium mb-5 flex items-center gap-2"><Clock className="w-5 h-5 text-brand" strokeWidth={1.5} /> Vaste week</h2>
        <div className="space-y-1">
          {hours.map((h) => (
            <div key={h.day} data-testid={`day-row-${h.day}`}
              className="flex flex-wrap items-center gap-3 py-2.5 border-b border-[#F2EBE1] last:border-0">
              <div className="w-28 shrink-0 font-medium">{DAYS[h.day]}</div>
              <Switch data-testid={`day-toggle-${h.day}`} checked={h.enabled}
                onCheckedChange={(v) => setDay(h.day, { enabled: v })} />
              {h.enabled ? (
                <div className="flex flex-col gap-2 ml-auto items-end">
                  {h.blocks.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input type="time" value={b.start} onChange={(e) => setBlock(h.day, i, { start: e.target.value })}
                        className="w-28 rounded-xl border-[#E8E2D9]" data-testid={i === 0 ? `day-start-${h.day}` : `day-start-${h.day}-${i}`} />
                      <span className="text-ink-muted">–</span>
                      <Input type="time" value={b.end} onChange={(e) => setBlock(h.day, i, { end: e.target.value })}
                        className="w-28 rounded-xl border-[#E8E2D9]" data-testid={i === 0 ? `day-end-${h.day}` : `day-end-${h.day}-${i}`} />
                      {h.blocks.length > 1
                        ? <Button size="icon" variant="ghost" onClick={() => removeBlock(h.day, i)} data-testid={`day-block-remove-${h.day}-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        : <span className="w-9 shrink-0" />}
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => addBlock(h.day)} data-testid={`day-block-add-${h.day}`} className="text-brand hover:bg-brand-light rounded-full h-8 px-3 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Extra blok</Button>
                </div>
              ) : (
                <span className="ml-auto text-sm text-ink-muted">Gesloten</span>
              )}
            </div>
          ))}
        </div>
        <Button onClick={saveHours} data-testid="save-hours-btn" className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white h-11 px-7">
          <Save className="w-4 h-4 mr-2" /> Opslaan
        </Button>
      </div>

      {/* Overrides */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mt-6">
        <h2 className="text-xl font-medium mb-1 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brand" strokeWidth={1.5} /> Losse dagen</h2>
        <p className="text-sm text-ink-muted mb-5">Blokkeer één dag of een hele periode (bijv. vakantie) of stel juist een normaal gesloten dag open. Laat "Tot datum" leeg voor één dag.</p>

        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div className="w-full sm:w-auto">
            <Label className="text-sm text-ink-muted mb-1.5 block">Van datum</Label>
            <Input type="date" value={ov.date} onChange={(e) => setOv({ ...ov, date: e.target.value })}
              className="w-full rounded-xl border-[#E8E2D9]" data-testid="override-date" />
          </div>
          <div className="w-full sm:w-auto">
            <Label className="text-sm text-ink-muted mb-1.5 block">Tot datum <span className="text-ink-muted/60">(optioneel)</span></Label>
            <Input type="date" value={ov.end_date} min={ov.date} onChange={(e) => setOv({ ...ov, end_date: e.target.value })}
              className="w-full rounded-xl border-[#E8E2D9]" data-testid="override-end-date" />
          </div>
          <div className="w-full sm:w-auto">
            <Label className="text-sm text-ink-muted mb-1.5 block">Type</Label>
            <Select value={ov.mode} onValueChange={(v) => setOv({ ...ov, mode: v })}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl border-[#E8E2D9]" data-testid="override-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="closed">Gesloten (blokkeren)</SelectItem>
                <SelectItem value="open">Extra open</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {ov.mode === "open" && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex-1 sm:flex-none">
                <Label className="text-sm text-ink-muted mb-1.5 block">Van</Label>
                <Input type="time" value={ov.start} onChange={(e) => setOv({ ...ov, start: e.target.value })} className="w-full sm:w-28 rounded-xl border-[#E8E2D9]" />
              </div>
              <div className="flex-1 sm:flex-none">
                <Label className="text-sm text-ink-muted mb-1.5 block">Tot</Label>
                <Input type="time" value={ov.end} onChange={(e) => setOv({ ...ov, end: e.target.value })} className="w-full sm:w-28 rounded-xl border-[#E8E2D9]" />
              </div>
            </div>
          )}
          <div className="w-full sm:flex-1 sm:min-w-[140px]">
            <Label className="text-sm text-ink-muted mb-1.5 block">Notitie</Label>
            <Input value={ov.note} onChange={(e) => setOv({ ...ov, note: e.target.value })} placeholder="bijv. Vakantie" className="w-full rounded-xl border-[#E8E2D9]" data-testid="override-note" />
          </div>
          <Button onClick={addOverride} data-testid="add-override-btn" className="w-full sm:w-auto rounded-full bg-brand hover:bg-brand-hover text-white h-11">
            <Plus className="w-4 h-4 mr-1" /> Toevoegen
          </Button>
        </div>

        {!overrides.length && <p className="text-sm text-ink-muted">Nog geen uitzonderingen.</p>}
        <div className="divide-y divide-[#F2EBE1]">
          {overrides.map((o) => (
            <div key={o.id} data-testid={`override-${o.id}`} className="flex items-center justify-between gap-4 py-3">
              <div>
                <div className="font-medium capitalize">{format(parseISO(o.date), "EEEE d MMMM yyyy", { locale: nl })}</div>
                <div className="text-sm text-ink-muted">
                  {o.mode === "closed"
                    ? <span className="text-destructive">Gesloten</span>
                    : <span className="text-green-600">Extra open · {o.start}–{o.end}</span>}
                  {o.note && <> · {o.note}</>}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => delOverride(o.id)} data-testid={`del-override-${o.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
